import { Component, effect, Input, OnInit, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconsComponent } from '@sml/icons';
import { ClickOutsideDirective, formatToMMDDYYYY, InputNumberDirective, IVolunteerShifts, PreventLeadingSpaceDirective, SlideInOutAnimation, timePatternValidator, TooltipDirective } from '@sml/utility';
import { DrawerComponent } from '@sml/drawer';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { RangePickerComponent } from '@sml/calendar';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { ButtonsModule } from '@sml/buttons';
import { DialogComponent } from '@sml/dialog';

@Component({
  selector: 'sml-item-participation-details',
  imports: [CommonModule,IconsComponent,DrawerComponent,FormsModule,ReactiveFormsModule,RangePickerComponent,    NgxMaskDirective,InputNumberDirective,ButtonsModule,DialogComponent,ClickOutsideDirective,TooltipDirective,PreventLeadingSpaceDirective
  ],
  providers: [provideNgxMask()],
  animations:[SlideInOutAnimation],
  
  templateUrl: './item-participation-details.component.html',
  styleUrl: './item-participation-details.component.scss',
})
export class ItemParticipationDetailsComponent implements OnInit,OnChanges {
  constructor(private readonly fb: FormBuilder){
    this.initForm();
  }
  @Input() contacts: IVolunteerShifts[] = [];
  @Input() isMobile: boolean = false;
  @Output() shiftsChange = new EventEmitter<IVolunteerShifts[]>();
  @Output () closeShiftModal = new EventEmitter<boolean>();
  @Input() drawerSize=false;
  @Input() isNewFlag=false;
  @ViewChild('smlModal') smlModal!: DialogComponent;
  isEdit=false;
  filesLength = 0;
  isDrawerOpen=false;
  isShiftEdit=false;
  public shiftForm!:FormGroup
  public selectedDate: Date | null = null;
  public showCalendar=false
  editingIndex: number | null = null;
  deleteShift=false;
  
  // Temporary properties to track AM/PM values during form input for new shifts
  tempStartTimeAPM: string = 'AM';
  tempEndTimeAPM: string = 'AM';
  
  handleClose(isOpen: boolean) {
    if(!this.drawerSize){
      this.deleteShift=false;
      this.closeShiftModal.emit(true);
      this.smlModal.openModal()
    }
    else{
      this.isDrawerOpen=false
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if(changes['isNewFlag'].currentValue==false ){
      this.shiftForm.get('quantity')?.clearValidators();
    }
  }
  backButton(){
    if(!this.drawerSize){
      this.deleteShift=false;
      this.closeShiftModal.emit(true);
      this.smlModal.openModal()
    }
    else{
      this.isDrawerOpen=false
    }
  }
  ngOnInit(): void {
    this.control['startTime']?.valueChanges.subscribe((val) => {
      if (val) {
        this.setDynamicValidators();
      }
    });
    this.control['endTime']?.valueChanges.subscribe((val) => {
      if (val) {
        this.setDynamicValidators();
      }
    });
    this.control['quantity']?.valueChanges.subscribe((val) => {
      if (val) {
        // Update quantity validator when quantity changes
        this.shiftForm.get('quantity')?.updateValueAndValidity();
      }
    });
    this.shiftsChange.emit(this.contacts);
  }
  initForm(): void {
    this.shiftForm = this.fb.group({
      id: [null],
      shiftTitle: [null,[Validators.required]],
      eventDate: [null,[Validators.required]],
      startTime:[null,[Validators.required,timePatternValidator()]],
      endTime:[null,[Validators.required,timePatternValidator()]],
      quantity:[null,[Validators.required, Validators.min(1)]],      
    });
  }
  onStartDate() {
    const startDateValue = this.shiftForm.get('eventDate')?.value;
    this.selectedDate = startDateValue ? new Date(startDateValue) : null;
    this.showCalendar = true;
  }
  
    onDateChange(date: Date | null): void {
      const formattedDate = formatToMMDDYYYY(date);
      this.shiftForm.patchValue({ eventDate: formattedDate });
      this.showCalendar = false;
      
    }
  
  // Helper methods to get AM/PM values for specific shifts
  getStartTimeAPM(shift: IVolunteerShifts): string {
    return shift.startTimeAPM || 'AM';
  }

  getEndTimeAPM(shift: IVolunteerShifts): string {
    return shift.endTimeAPM || 'AM';
  }
   fixTimeOnBlur(controlName: 'startTime' | 'endTime') {
    const control = this.shiftForm.get(controlName);
    const value: string = control?.value || '';
    if (!value) return;

    let fixedValue = value;
    if (value.length === 1) {
        fixedValue = (value === '0' || value === '1' ? '0' + value : value) + '00';
    } else if (value.length === 2) {
      if (parseInt(value, 10) <= 12) {
        fixedValue = value + '00';
      } else {
        // e.g. "13" → first digit is hour(1), second is units-of-minutes → "0103"
        // e.g. "84" → first digit is hour, second is units-of-minutes → "804"
        fixedValue = (value[0] === '1' ? '0' : '') + value[0] + '0' + value[1];
      }
    } else if (value.length === 3) {
      const hourPart = parseInt(value.substring(0, 2), 10);
      const thirdDigit = parseInt(value[2], 10);

      if (hourPart <= 12) {
        // Valid 2-digit hour; third digit is a minute digit
        if (thirdDigit > 5) {
          // Can't be tens-of-minutes; ngx-mask auto-inserted 0 for tens → "118" → "1108"
          fixedValue = value.substring(0, 2) + '0' + value[2];
        } else {
          // Tens-of-minutes present; pad units with 0 → "123" → "1230"
          fixedValue = value + '0';
        }
      } else {
        // First digit alone is the hour, last two are minutes — already correctly formed
        // e.g. "730" stays "730" → displays as "7:30" (no leading zero)
        fixedValue = value;
      }
    }

    // In 12-hour format, hour "00" is invalid — midnight is 12:00 AM
    if (fixedValue.length === 4 && fixedValue.startsWith('00')) {
      fixedValue = '12' + fixedValue.substring(2);
    }

    if (fixedValue !== value) {
      control?.setValue(fixedValue);
    }
  }

  // Get AM/PM values for the current shift being edited
  getCurrentStartTimeAPM(): string {
    if (this.editingIndex !== null && this.editingIndex >= 0 && this.contacts[this.editingIndex]) {
      return this.contacts[this.editingIndex].startTimeAPM || 'AM';
    }
    return this.tempStartTimeAPM;
  }

  getCurrentEndTimeAPM(): string {
    if (this.editingIndex !== null && this.editingIndex >= 0 && this.contacts[this.editingIndex]) {
      return this.contacts[this.editingIndex].endTimeAPM || 'AM';
    }
    return this.tempEndTimeAPM;
  }

  toggleAMPM(val: string) {
    if (val === 'start') {
      if (this.editingIndex !== null && this.editingIndex >= 0 && this.contacts[this.editingIndex]) {
        // Editing existing shift
        const currentShift = this.contacts[this.editingIndex];
        currentShift.startTimeAPM = currentShift.startTimeAPM === 'AM' ? 'PM' : 'AM';
      } else {
        // Creating new shift
        this.tempStartTimeAPM = this.tempStartTimeAPM === 'AM' ? 'PM' : 'AM';
      }
      this.setDynamicValidators();
    } else if (val === 'end') {
      if (this.editingIndex !== null && this.editingIndex >= 0 && this.contacts[this.editingIndex]) {
        // Editing existing shift
        const currentShift = this.contacts[this.editingIndex];
        currentShift.endTimeAPM = currentShift.endTimeAPM === 'AM' ? 'PM' : 'AM';
      } else {
        // Creating new shift
        this.tempEndTimeAPM = this.tempEndTimeAPM === 'AM' ? 'PM' : 'AM';
      }
      this.setDynamicValidators();
    }
  }
    setDynamicValidators() {
      this.shiftForm.setValidators(this.timeRangeValidator());
      this.shiftForm.updateValueAndValidity();
    }
    formatTime(time: string): string {
      if (time.length === 3) {
        return `${time[0]}:${time.slice(1)}`;
      }
      const hours = parseInt(time.slice(0, 2), 10);
      const minutes = time.slice(2, 4);
      return `${hours}:${minutes}`;
    }
    
      timeRangeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
          const startTime = control.get('startTime')?.value;
          const endTime = control.get('endTime')?.value;
          const startTimeAPMPM = this.getCurrentStartTimeAPM();
          const endTimeAMPM = this.getCurrentEndTimeAPM();
    
          if (startTime && endTime) {
            const startTimeDate = this.convertToDateWithAMPM(
              startTime,
              startTimeAPMPM
            );
            const endTimeDate = this.convertToDateWithAMPM(endTime, endTimeAMPM);
    
            if (endTimeDate < startTimeDate) {
              return { endTimeLessThanStart: true };
            }
            if( endTimeDate.getTime() === startTimeDate.getTime()) {
              return { endTimeEqualToStart: true };
            }
         
          }
    
          if (!startTime && endTime) {
            return { startTimeRequired: true };
          } else if (startTime && !endTime) {
            return { endTimeRequired: true };
          }
    
          return null;
        };
      }
      convertToDateWithAMPM(timeStr: string, amPm: string): Date {
        const padded = timeStr.padStart(4, '0');
        let hours = parseInt(padded.substring(0, 2), 10);
        const minutes = parseInt(padded.substring(2, 4), 10);
    
        if (amPm === 'AM') {
          if (hours === 12) {
            hours = 0;
          }
        } else if (amPm === 'PM') {
          if (hours !== 12) {
            hours += 12;
          }
        }
    
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentDay = currentDate.getDate();
    
        return new Date(currentYear, currentMonth, currentDay, hours, minutes);
      }

  quantityValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; 
      }
      
      const quantity = control.value;
      const currentShift = this.contacts[this.editingIndex ?? -1];
      
      if (this.isShiftEdit && currentShift && currentShift.donationCompletedCount) {
        if (quantity < currentShift.donationCompletedCount) {
          return { 
            quantityTooLow: { 
              message: `Quantity cannot be less than ${currentShift.donationCompletedCount} (completed donations)` 
            } 
          };
        }
      }
      
      return null;
    };
  }

  get control() {
    return this.shiftForm.controls;
  }
  openDrawer(){
    this.isDrawerOpen=true;
    this.isShiftEdit=false;
    this.editingIndex = null;
    this.shiftForm.reset();
    // Reset temporary AM/PM values for new shifts
    this.tempStartTimeAPM = 'AM';
    this.tempEndTimeAPM = 'AM';
    // Ensure the quantity validator is properly set up for new shifts
    this.shiftForm.get('quantity')?.setValidators([Validators.required, Validators.min(1)]);
    this.shiftForm.get('quantity')?.updateValueAndValidity();
  }
  closeGridButton(){
    if(!this.drawerSize){
      this.deleteShift=false;
      this.smlModal.openModal();
    }
    else{
      this.isDrawerOpen=false;
      this.shiftForm.reset();
      this.editingIndex = null;
      this.isShiftEdit = false;
      // Reset temporary AM/PM values
      this.tempStartTimeAPM = 'AM';
      this.tempEndTimeAPM = 'AM';
      // Ensure the quantity validator is properly set up after reset
      this.shiftForm.get('quantity')?.updateValueAndValidity();
    }
  }
  onCloseDialog(){
    this.smlModal.closeModal();
  }
  onSaveChanges(){
    if(this.deleteShift){
       if (this.editingIndex != null && this.editingIndex >= 0) {
         this.contacts.splice(this.editingIndex, 1);
         this.isDrawerOpen = false; 
         this.editingIndex = -1;  
    }
    this.shiftForm.reset();
    this.shiftsChange.emit(this.contacts);
  }
  else{
    this.shiftForm.reset();
    this.isDrawerOpen=false;
    }
    this.editingIndex = null;
    this.isShiftEdit =false;
    this.smlModal.closeModal();
    // Reset temporary AM/PM values
    this.tempStartTimeAPM = 'AM';
    this.tempEndTimeAPM = 'AM';
    // Ensure the quantity validator is properly set up after reset
    this.shiftForm.get('quantity')?.updateValueAndValidity();
  }
  
  saveGridButton(){
    if(!this.shiftForm.valid){
      this.shiftForm.markAllAsTouched();
      return;
    }
    const newShift = this.shiftForm.value;
    
    // Set AM/PM values for the new/edited shift
    if (this.editingIndex !== null) {
      const existingShift = this.contacts[this.editingIndex];
      newShift.id = existingShift.id;
      // Preserve AM/PM values from the existing shift
      newShift.startTimeAPM = existingShift.startTimeAPM || 'AM';
      newShift.endTimeAPM = existingShift.endTimeAPM || 'AM';
      this.contacts[this.editingIndex] = newShift; 
    } else {
      // For new shifts, use the temporary AM/PM values
      newShift.startTimeAPM = this.tempStartTimeAPM;
      newShift.endTimeAPM = this.tempEndTimeAPM;
      this.contacts.push(newShift);
    }
    this.shiftsChange.emit(this.contacts);

    this.shiftForm.reset();
    this.isDrawerOpen = false;
    this.editingIndex = null;
    this.isShiftEdit = false;
    // Ensure the quantity validator is properly set up after reset
    this.shiftForm.get('quantity')?.updateValueAndValidity();
  }
  editShift(item:IVolunteerShifts){
    this.isDrawerOpen=true;
    this.isShiftEdit=true;
    this.editingIndex = this.contacts.findIndex(contact => contact === item); 
    this.shiftForm.patchValue({
      shiftTitle:item.shiftTitle,
      eventDate:item.eventDate,
      startTime:item.startTime,
      endTime:item.endTime,
      quantity:item.quantity,
      id:item.id
    });
    if(item.id){
      this.shiftForm.get('quantity')?.clearValidators();
    }
    else{
      this.shiftForm.get('quantity')?.setValidators([Validators.required, Validators.min(1)]);
    }
    this.shiftForm.updateValueAndValidity();
    // Set temporary AM/PM values for display in the form
    this.tempStartTimeAPM = item.startTimeAPM || 'AM';
    this.tempEndTimeAPM = item.endTimeAPM || 'AM';
    
    // Update the quantity validator after patching values
    this.shiftForm.get('quantity')?.updateValueAndValidity();
  }
  removeShift(){
    if(!this.drawerSize){
      this.deleteShift=true;
      this.smlModal.openModal();
    }
    else{
      if (this.editingIndex != null && this.editingIndex >= 0) {
        this.contacts.splice(this.editingIndex, 1);
        this.isDrawerOpen = false; 
        this.editingIndex = -1;
        this.isShiftEdit = false;
        this.shiftForm.reset();
        // Reset temporary AM/PM values
        this.tempStartTimeAPM = 'AM';
        this.tempEndTimeAPM = 'AM';
      }
    }
    this.shiftsChange.emit(this.contacts);

  }
}